import { useState } from 'react';

import { ActionStepTemplate } from 'types/index';

import TemplateCard from './TemplateCard';

import Button from '@mui/material/Button';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        width: '100%'
    },
    expandButton: {
        display: 'flex',
        alignItems: 'center',
        alignSelf: 'center',
        justifyContent: 'center',
        gap: '8px',
        backgroundColor: '#FFFFFF',
        borderRadius: '20px',
        boxShadow: '0px 8px 16px 4px #0000000D',
        color: '#A6A6BF',
        fontWeight: 600,
        padding: '10px 20px',
        textTransform: 'none',
        width: 'fit-content'
    },
    listContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
    }
});

type ActionStepSuggestionListProps = {
    items: ActionStepTemplate[];
    onItemClick: (item: ActionStepTemplate, index: number) => void;
    onSeeMore?: () => void;
    hasMore?: boolean;
    initialDisplayCount?: number;
    loading?: boolean;
    enableOverlay?: boolean;
};

const ActionStepSuggestionList: React.FC<ActionStepSuggestionListProps> = ({
    items,
    onItemClick,
    onSeeMore,
    hasMore = false,
    initialDisplayCount = 2,
    loading = false,
    enableOverlay = false
}) => {
    const classes = useStyles();
    const [displayCount, setDisplayCount] = useState(initialDisplayCount);
    const [overlay, setOverlay] = useState<{
        index: null | number;
        show: boolean;
    }>({
        index: null,
        show: false
    });
    const [disabled, setDisabled] = useState(false);

    const handleSeeMoreClick = () => {
        if (onSeeMore) {
            // If onSeeMore is provided, call it (for network loading)
            onSeeMore();
        } else {
            // Otherwise, show more from preloaded data
            setDisplayCount((prevCount) => prevCount + 2);
        }
    };

    const handleItemClick = (item: ActionStepTemplate, index: number) => {
        if (enableOverlay) {
            setDisabled(true);
            setOverlay({
                index: index,
                show: true
            });

            // Show overlay for 1.5 seconds, then call the parent handler
            setTimeout(() => {
                onItemClick(item, index);
            }, 1500);
        } else {
            // If no overlay needed, call handler immediately
            onItemClick(item, index);
        }
    };

    const shouldShowSeeMore = onSeeMore ? hasMore : displayCount < items.length;

    return (
        <div className={classes.container}>
            <div className={classes.listContainer}>
                {items.slice(0, onSeeMore ? items.length : displayCount).map((item, index) => (
                    <TemplateCard
                        key={index}
                        disabled={loading || disabled}
                        onClick={() => handleItemClick(item, index)}
                        overlay={overlay.show && overlay.index === index}
                        template={item}
                    />
                ))}
                {shouldShowSeeMore && (
                    <Button
                        className={classes.expandButton}
                        onClick={handleSeeMoreClick}
                        disabled={loading}
                    >
                        {loading ? 'Loading...' : 'See More'} <i className="fal fa-chevron-down" />
                    </Button>
                )}
            </div>
        </div>
    );
};

export default ActionStepSuggestionList;
